import { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { checkInUser } from '@api'
import { getAuthToken } from '@common/auth'
import { commitPermissionsVersion } from '@common/permissionsVersion'
import { setPermissions, setPermissionVersion } from '@store/reducers/auth'
import { setUserData, selectedGroup } from '@reducer/profile'
import { PermissionITF, PERMISSION_DEFAULTS } from '@/authProvider'
import { RootStateITF } from '@store/redux-types'
import { UserITF } from '@/types'

/**
 * Refreshes permissions when the backend signals a version change via the
 * `Permissions-Version` response header (piggybacked on every CSM response).
 *
 * The axios interceptor in axiosService.ts compares the header against the
 * last-seen value and dispatches a `permissions-version-changed` DOM event
 * when it differs. This hook listens for that event, debounces rapid changes,
 * and calls CheckIn to refetch the full permission set.
 *
 * Also refreshes on tab visibility change to catch updates missed while backgrounded.
 */
const usePermissionRefresh = () => {
  const dispatch = useDispatch()
  const selectedGroupId = useSelector((state: RootStateITF) => state.profile.selectedGroupId)
  const user = useSelector((state: { profile: { user?: UserITF } }) => state.profile?.user)

  const currentPermissions = useSelector((state: RootStateITF) => state.auth.permissions)

  const selectedGroupIdRef = useRef(selectedGroupId)
  selectedGroupIdRef.current = selectedGroupId
  const userRef = useRef(user)
  userRef.current = user
  const currentPermissionsRef = useRef(currentPermissions)
  currentPermissionsRef.current = currentPermissions
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  // Header version awaiting a successful refresh. Committed to the axios-side
  // tracker only once CheckIn succeeds; on failure it stays pending so the next
  // response's header retriggers the refresh instead of leaving permissions stale.
  const pendingVersionRef = useRef<string | null>(null)

  const refreshPermissions = () => {
    if (!getAuthToken() || !selectedGroupIdRef.current) return
    const sentGroupId = selectedGroupIdRef.current
    checkInUser<UserITF>({ GroupId: sentGroupId })
      .then((response) => {
        if (pendingVersionRef.current) {
          commitPermissionsVersion(pendingVersionRef.current)
          pendingVersionRef.current = null
        }
        // The backend resolves a stale/deleted GroupId to a real fallback and
        // reports the effective group in UserContext. Adopt it when it differs
        // from what we sent — otherwise the poisoned persisted selection
        // replays on every tab focus and permission refresh forever.
        const ctx = response?.UserContext
        if (ctx?.CurrentGroupId && ctx.CurrentGroupId !== sentGroupId) {
          dispatch(
            selectedGroup({
              groupId: ctx.CurrentGroupId,
              groupName: ctx.CurrentGroupName?.en,
              group: {
                acId: ctx.CurrentGroupId,
                GroupName: ctx.CurrentGroupName,
                LogoFilename: ctx.CurrentGroupLogoFilename,
              },
            })
          )
        }
        if (!response?.UserContext?.PermissionsHash) return
        const updated: PermissionITF = {
          ...PERMISSION_DEFAULTS,
          ...response.UserContext.PermissionsHash,
          PortfolioTeam:
            'PortfolioTeam' in response
              ? !!response.PortfolioTeam
              : !!currentPermissionsRef.current?.PortfolioTeam,
        }
        // A null user must be bootstrapped from the CheckIn response, not
        // skipped: the layout renders <Loading/> forever on user == null and
        // this refresh may be the only fetch the app ever makes in that state.
        const currentUser = userRef.current
        dispatch(setUserData(currentUser ? { ...currentUser, UserContext: response.UserContext } : response))
        dispatch(setPermissions(updated))
        if (typeof response.UserContext?.PermissionVersion === 'number') {
          dispatch(setPermissionVersion(response.UserContext.PermissionVersion))
        }
      })
      .catch(() => {})
  }

  // Refresh on Permissions-Version header change (debounced)
  useEffect(() => {
    const handleVersionChange = (event: Event) => {
      pendingVersionRef.current = (event as CustomEvent<string>).detail ?? null
      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(refreshPermissions, 300)
    }

    window.addEventListener('permissions-version-changed', handleVersionChange)
    return () => {
      clearTimeout(debounceRef.current)
      window.removeEventListener('permissions-version-changed', handleVersionChange)
    }
  }, [dispatch])

  // Refresh on tab visibility change (catches updates missed while backgrounded)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshPermissions()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [dispatch])
}

export default usePermissionRefresh
